import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  FileText, ArrowLeft, Upload, Plus, Trash2, Edit3, TrendingUp,
  TrendingDown, GitCompare, Loader2, FileImage, X, Save, Lock, Building2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicesPage() {
  // Auth
  const [loggedIn, setLoggedIn] = useState(false);
  const [pin, setPin] = useState("");

  // Data
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [activeInvoice, setActiveInvoice] = useState(null); // full invoice for edit view

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadNote, setUploadNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Vendor mgmt
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: "", extraction_hints: "" });
  const [editVendor, setEditVendor] = useState(null);

  // Comparison
  const [compareMode, setCompareMode] = useState(false);
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);

  // ---------- Auth ----------
  const handleLogin = async () => {
    try {
      await axios.post(`${API}/invoices/login`, { pin });
      setLoggedIn(true);
      localStorage.setItem("invoice_pin_ok", "1");
      toast.success("Welcome");
    } catch {
      toast.error("Invalid PIN");
    }
  };

  useEffect(() => {
    if (localStorage.getItem("invoice_pin_ok") === "1") setLoggedIn(true);
  }, []);

  // ---------- Load data ----------
  const loadVendors = async () => {
    try {
      const res = await axios.get(`${API}/invoices/vendors`);
      setVendors(res.data);
    } catch (e) {
      toast.error("Failed to load vendors");
    }
  };

  const loadInvoices = async (vendorId) => {
    try {
      const res = await axios.get(`${API}/invoices/`, { params: { vendor_id: vendorId } });
      setInvoices(res.data);
    } catch {
      toast.error("Failed to load invoices");
    }
  };

  useEffect(() => {
    if (loggedIn) loadVendors();
  }, [loggedIn]);

  useEffect(() => {
    if (selectedVendor) {
      loadInvoices(selectedVendor.id);
      setComparison(null);
      setActiveInvoice(null);
    }
  }, [selectedVendor]);

  // ---------- Vendor actions ----------
  const addVendor = async () => {
    if (!newVendor.name.trim()) return toast.error("Vendor name required");
    try {
      await axios.post(`${API}/invoices/vendors`, newVendor);
      toast.success("Vendor added");
      setNewVendor({ name: "", extraction_hints: "" });
      setShowAddVendor(false);
      loadVendors();
    } catch {
      toast.error("Failed to add vendor");
    }
  };

  const updateVendor = async () => {
    try {
      await axios.put(`${API}/invoices/vendors/${editVendor.id}`, {
        name: editVendor.name,
        extraction_hints: editVendor.extraction_hints || "",
      });
      toast.success("Vendor updated");
      setEditVendor(null);
      loadVendors();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm("Delete this vendor? Invoices already uploaded will remain.")) return;
    try {
      await axios.delete(`${API}/invoices/vendors/${id}`);
      toast.success("Vendor deleted");
      if (selectedVendor?.id === id) setSelectedVendor(null);
      loadVendors();
    } catch {
      toast.error("Failed");
    }
  };

  // ---------- Upload ----------
  const handleFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    setUploadFiles(prev => [...prev, ...files]);
  };

  const removeFile = (idx) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const submitUpload = async () => {
    if (!selectedVendor) return toast.error("Pick a vendor first");
    if (uploadFiles.length === 0) return toast.error("Add at least 1 page");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("vendor_id", selectedVendor.id);
      fd.append("note", uploadNote);
      // Compress each image to JPEG max 1600px wide before upload (reduces payload + AI processing time)
      for (const f of uploadFiles) {
        const compressed = await compressImage(f, 1600, 0.85);
        fd.append("files", compressed, compressed.name);
      }
      const res = await axios.post(`${API}/invoices/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000, // 4 min
      });
      toast.success(`Extracted ${res.data.products.length} products via ${res.data.extracted_by}`);
      setShowUpload(false);
      setUploadFiles([]);
      setUploadNote("");
      loadInvoices(selectedVendor.id);
      openInvoice(res.data.id);
    } catch (e) {
      const serverMsg = e.response?.data?.detail || e.response?.statusText;
      const httpStatus = e.response?.status;
      const networkMsg = e.code === "ECONNABORTED" ? "Request timed out (took > 4 min)" : e.message;
      const msg = serverMsg ? `Server error ${httpStatus}: ${serverMsg}` : `Upload failed: ${networkMsg}`;
      toast.error(msg, { duration: 10000 });
      console.error("Invoice upload error:", e);
    } finally {
      setUploading(false);
    }
  };

  // Compress an image File to a JPEG of max given width, returning a File
  const compressImage = (file, maxWidth = 1600, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => { img.src = e.target.result; };
      reader.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("compression failed"));
            const newName = file.name.replace(/\.[^.]+$/, "") + ".jpg";
            resolve(new File([blob], newName, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // ---------- Invoice actions ----------
  const openInvoice = async (id) => {
    try {
      const res = await axios.get(`${API}/invoices/${id}`);
      setActiveInvoice(res.data);
    } catch {
      toast.error("Failed to load invoice");
    }
  };

  const saveInvoiceEdits = async () => {
    try {
      await axios.put(`${API}/invoices/${activeInvoice.id}`, {
        products: activeInvoice.products,
        invoice_number: activeInvoice.invoice_number,
        invoice_date: activeInvoice.invoice_date,
        note: activeInvoice.note,
      });
      toast.success("Saved");
      loadInvoices(selectedVendor.id);
    } catch {
      toast.error("Failed to save");
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success("Deleted");
      if (activeInvoice?.id === id) setActiveInvoice(null);
      loadInvoices(selectedVendor.id);
    } catch {
      toast.error("Failed");
    }
  };

  const updateProductLine = (idx, field, value) => {
    const next = { ...activeInvoice };
    next.products = [...next.products];
    next.products[idx] = { ...next.products[idx], [field]: value };
    setActiveInvoice(next);
  };

  const addProductLine = () => {
    setActiveInvoice({
      ...activeInvoice,
      products: [
        ...activeInvoice.products,
        { upc: "", sku: "", description: "", pack_size: "", qty: 0, unit_price: 0, line_total: 0 },
      ],
    });
  };

  const removeProductLine = (idx) => {
    setActiveInvoice({
      ...activeInvoice,
      products: activeInvoice.products.filter((_, i) => i !== idx),
    });
  };

  // ---------- Compare ----------
  const runAutoCompare = async () => {
    if (!selectedVendor) return;
    setComparing(true);
    try {
      const res = await axios.get(`${API}/invoices/compare/auto/${selectedVendor.id}`);
      setComparison(res.data);
      setCompareMode(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compare failed");
    } finally {
      setComparing(false);
    }
  };

  const runManualCompare = async () => {
    if (!compareA || !compareB) return toast.error("Pick both invoices");
    if (compareA === compareB) return toast.error("Pick two different invoices");
    setComparing(true);
    try {
      const res = await axios.get(`${API}/invoices/compare/diff`, {
        params: { invoice_a: compareA, invoice_b: compareB },
      });
      setComparison(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compare failed");
    } finally {
      setComparing(false);
    }
  };

  // ---------- Render ----------
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1A1A1A] to-[#2D2D2D] flex flex-col">
        <header className="bg-[#1A1A1A] border-b border-[#333] py-4 px-4">
          <div className="max-w-md mx-auto flex justify-between items-center">
            <Link to="/" className="text-white hover:text-[#D4AF37]"><ArrowLeft className="w-6 h-6" /></Link>
            <h1 className="text-white font-bold text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" /> Invoice Vault
            </h1>
            <div className="w-6"></div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#1A5E1F] rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Invoice Vault</h2>
              <p className="text-[#666] mt-1">Enter PIN to continue</p>
            </div>
            <Input
              type="password"
              placeholder="PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="text-center text-2xl font-bold h-14 mb-4"
              onKeyPress={(e) => e.key === "Enter" && handleLogin()}
              data-testid="invoice-pin-input"
            />
            <Button onClick={handleLogin} className="w-full bg-[#1A5E1F] hover:bg-[#155219] h-12 text-lg" data-testid="invoice-pin-submit">
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main app
  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <header className="bg-[#1A1A1A] text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="text-white hover:text-[#D4AF37] flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" /> <span className="font-bold">Invoice Vault</span>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { localStorage.removeItem("invoice_pin_ok"); setLoggedIn(false); }}
            className="border-white text-white hover:bg-white hover:text-[#1A1A1A]"
          >
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-3 grid md:grid-cols-[260px_1fr] gap-3">
        {/* Vendor sidebar */}
        <aside className="bg-white rounded-lg p-3 h-fit">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-bold text-[#1A1A1A] flex items-center gap-2 text-sm"><Building2 className="w-4 h-4" /> Vendors</h2>
            <Button size="sm" onClick={() => setShowAddVendor(true)} className="h-7 bg-[#1A5E1F]" data-testid="btn-add-vendor">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-1">
            {vendors.map((v) => (
              <div key={v.id} className={`group flex justify-between items-center px-2 py-2 rounded text-sm cursor-pointer ${
                selectedVendor?.id === v.id ? "bg-[#A61B1B] text-white" : "hover:bg-gray-100"
              }`}>
                <span onClick={() => setSelectedVendor(v)} className="flex-1" data-testid={`vendor-item-${v.name.replace(/\s+/g,'-').toLowerCase()}`}>{v.name}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => setEditVendor({ ...v })} className="p-0.5">
                    <Edit3 className={`w-3.5 h-3.5 ${selectedVendor?.id === v.id ? "text-white" : "text-[#666]"}`} />
                  </button>
                  <button onClick={() => deleteVendor(v.id)} className="p-0.5">
                    <Trash2 className={`w-3.5 h-3.5 ${selectedVendor?.id === v.id ? "text-white" : "text-[#A61B1B]"}`} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main className="space-y-3">
          {!selectedVendor ? (
            <div className="bg-white rounded-lg p-8 text-center text-[#666]">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              Select a vendor to view invoices, or add a new one.
            </div>
          ) : (
            <>
              {/* Vendor header */}
              <div className="bg-white rounded-lg p-4 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-[#1A1A1A]">{selectedVendor.name}</h2>
                  <p className="text-xs text-[#666]">{invoices.length} invoice(s) on file</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={runAutoCompare} disabled={invoices.length < 2 || comparing} variant="outline" className="border-[#A61B1B] text-[#A61B1B]" data-testid="btn-auto-compare">
                    {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4 mr-1" />}
                    Compare Latest 2
                  </Button>
                  <Button onClick={() => setShowUpload(true)} className="bg-[#1A5E1F]" data-testid="btn-upload-invoice">
                    <Upload className="w-4 h-4 mr-1" /> Upload Invoice
                  </Button>
                </div>
              </div>

              {/* Manual compare picker */}
              {invoices.length >= 2 && (
                <div className="bg-white rounded-lg p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium text-[#666]">Manual compare:</span>
                    <select value={compareA} onChange={(e) => setCompareA(e.target.value)} className="border rounded p-1.5 text-xs" data-testid="select-compare-a">
                      <option value="">Newer invoice…</option>
                      {invoices.map(i => (
                        <option key={i.id} value={i.id}>
                          {i.invoice_date || i.uploaded_at.slice(0, 10)} · {i.invoice_number || "no#"} · {i.product_count} items
                        </option>
                      ))}
                    </select>
                    <span>vs</span>
                    <select value={compareB} onChange={(e) => setCompareB(e.target.value)} className="border rounded p-1.5 text-xs" data-testid="select-compare-b">
                      <option value="">Older invoice…</option>
                      {invoices.map(i => (
                        <option key={i.id} value={i.id}>
                          {i.invoice_date || i.uploaded_at.slice(0, 10)} · {i.invoice_number || "no#"} · {i.product_count} items
                        </option>
                      ))}
                    </select>
                    <Button size="sm" onClick={runManualCompare} disabled={comparing} className="bg-[#A61B1B] h-8">
                      Compare
                    </Button>
                  </div>
                </div>
              )}

              {/* Comparison results */}
              {comparison && (
                <ComparisonView comparison={comparison} onClose={() => setComparison(null)} />
              )}

              {/* Active invoice editor */}
              {activeInvoice && (
                <InvoiceEditor
                  invoice={activeInvoice}
                  onClose={() => setActiveInvoice(null)}
                  onSave={saveInvoiceEdits}
                  onChange={setActiveInvoice}
                  updateProductLine={updateProductLine}
                  addProductLine={addProductLine}
                  removeProductLine={removeProductLine}
                  onDelete={() => deleteInvoice(activeInvoice.id)}
                />
              )}

              {/* Invoice list */}
              {!activeInvoice && (
                <div className="bg-white rounded-lg p-3">
                  <h3 className="font-bold text-sm text-[#1A1A1A] mb-2">Invoices</h3>
                  {invoices.length === 0 ? (
                    <p className="text-sm text-[#666] py-6 text-center">No invoices yet. Click "Upload Invoice" to start.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {invoices.map((inv) => (
                        <div key={inv.id} className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer text-sm"
                             onClick={() => openInvoice(inv.id)} data-testid={`invoice-row-${inv.id}`}>
                          <div className="flex-1">
                            <div className="font-medium text-[#1A1A1A]">
                              {inv.invoice_date || "—"} · #{inv.invoice_number || "—"}
                            </div>
                            <div className="text-xs text-[#666]">
                              {inv.product_count} products · ${inv.total_amount?.toFixed(2) || "0.00"} · uploaded {new Date(inv.uploaded_at).toLocaleDateString()} · via {inv.extracted_by}
                            </div>
                            {inv.note && <div className="text-xs text-[#A78B00] italic">{inv.note}</div>}
                          </div>
                          <Button size="sm" variant="ghost" className="h-7"><Edit3 className="w-3.5 h-3.5" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 max-w-lg w-full">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-lg">Upload Invoice — {selectedVendor?.name}</h3>
              <button onClick={() => setShowUpload(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-[#666] mb-3">Add 1 photo per page. All pages should belong to the same invoice. Supports JPG, PNG, WEBP.</p>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/jpg"
              multiple
              onChange={handleFilesPicked}
              className="hidden"
              data-testid="invoice-file-input"
            />
            <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="w-full mb-3 border-dashed border-2">
              <FileImage className="w-4 h-4 mr-2" /> Add Page(s) ({uploadFiles.length} selected)
            </Button>

            {uploadFiles.length > 0 && (
              <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
                {uploadFiles.map((f, i) => (
                  <div key={i} className="flex justify-between items-center px-2 py-1.5 bg-gray-50 rounded text-xs">
                    <span className="truncate">{i + 1}. {f.name} ({(f.size/1024).toFixed(0)} KB)</span>
                    <button onClick={() => removeFile(i)}><X className="w-3.5 h-3.5 text-[#A61B1B]" /></button>
                  </div>
                ))}
              </div>
            )}

            <Textarea
              placeholder="Note (optional) — e.g. 'Week of 5/15'"
              value={uploadNote}
              onChange={(e) => setUploadNote(e.target.value)}
              className="mb-3 text-sm"
              rows={2}
            />

            <Button onClick={submitUpload} disabled={uploading || uploadFiles.length === 0} className="w-full bg-[#1A5E1F] h-11" data-testid="btn-submit-upload">
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting (this may take 30-90s)…</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Extract & Save</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Add Vendor modal */}
      {showAddVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 max-w-md w-full">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-lg">Add Vendor</h3>
              <button onClick={() => setShowAddVendor(false)}><X className="w-5 h-5" /></button>
            </div>
            <Input
              placeholder="Vendor name"
              value={newVendor.name}
              onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
              className="mb-2"
              data-testid="new-vendor-name"
            />
            <Textarea
              placeholder="Extraction hints (optional) — describe this vendor's invoice layout to train the AI. E.g. 'SKUs are 7 digits, prices in right column, pack size as 12/16OZ'"
              value={newVendor.extraction_hints}
              onChange={(e) => setNewVendor({ ...newVendor, extraction_hints: e.target.value })}
              className="mb-3 text-sm"
              rows={4}
            />
            <Button onClick={addVendor} className="w-full bg-[#1A5E1F]" data-testid="btn-save-vendor">Add Vendor</Button>
          </div>
        </div>
      )}

      {/* Edit vendor modal */}
      {editVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 max-w-md w-full">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-lg">Edit Vendor</h3>
              <button onClick={() => setEditVendor(null)}><X className="w-5 h-5" /></button>
            </div>
            <Input
              value={editVendor.name}
              onChange={(e) => setEditVendor({ ...editVendor, name: e.target.value })}
              className="mb-2"
            />
            <Textarea
              placeholder="Extraction hints"
              value={editVendor.extraction_hints || ""}
              onChange={(e) => setEditVendor({ ...editVendor, extraction_hints: e.target.value })}
              className="mb-3 text-sm"
              rows={5}
            />
            <p className="text-xs text-[#666] mb-3">Tip: After each successful invoice extraction, refine these hints based on what was missed or misread. The AI uses them as context every upload.</p>
            <Button onClick={updateVendor} className="w-full bg-[#1A5E1F]">Save Changes</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Sub-components ============

function InvoiceEditor({ invoice, onClose, onSave, updateProductLine, addProductLine, removeProductLine, onDelete, onChange }) {
  return (
    <div className="bg-white rounded-lg p-3">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="font-bold text-[#1A1A1A]">Invoice Details</h3>
          <p className="text-xs text-[#666]">Extracted via {invoice.extracted_by} · {invoice.products.length} lines · Edit any errors and Save to train future extractions.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={onSave} className="bg-[#1A5E1F]" data-testid="btn-save-invoice"><Save className="w-4 h-4 mr-1" />Save</Button>
          <Button size="sm" variant="outline" className="text-[#A61B1B] border-[#A61B1B]" onClick={onDelete}><Trash2 className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
        <div>
          <label className="text-[#666]">Invoice #</label>
          <Input value={invoice.invoice_number} onChange={(e) => onChange({ ...invoice, invoice_number: e.target.value })} className="h-8 text-sm" />
        </div>
        <div>
          <label className="text-[#666]">Date</label>
          <Input value={invoice.invoice_date} placeholder="YYYY-MM-DD" onChange={(e) => onChange({ ...invoice, invoice_date: e.target.value })} className="h-8 text-sm" />
        </div>
        <div>
          <label className="text-[#666]">Note</label>
          <Input value={invoice.note || ""} onChange={(e) => onChange({ ...invoice, note: e.target.value })} className="h-8 text-sm" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-[#1A1A1A] text-white">
              <th className="px-1 py-1 text-left border border-[#333]">#</th>
              <th className="px-1 py-1 text-left border border-[#333]">UPC</th>
              <th className="px-1 py-1 text-left border border-[#333]">SKU</th>
              <th className="px-1 py-1 text-left border border-[#333] min-w-[200px]">Description</th>
              <th className="px-1 py-1 text-left border border-[#333]">Pack</th>
              <th className="px-1 py-1 text-right border border-[#333]">Qty</th>
              <th className="px-1 py-1 text-right border border-[#333]">Unit $</th>
              <th className="px-1 py-1 text-right border border-[#333]">Total $</th>
              <th className="px-1 py-1 border border-[#333]"></th>
            </tr>
          </thead>
          <tbody>
            {invoice.products.map((p, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-1 py-0.5 border border-gray-200 text-[#666]">{idx + 1}</td>
                <td className="border border-gray-200"><input value={p.upc} onChange={(e) => updateProductLine(idx, "upc", e.target.value)} className="w-24 px-1 py-0.5 text-xs" /></td>
                <td className="border border-gray-200"><input value={p.sku} onChange={(e) => updateProductLine(idx, "sku", e.target.value)} className="w-20 px-1 py-0.5 text-xs" /></td>
                <td className="border border-gray-200"><input value={p.description} onChange={(e) => updateProductLine(idx, "description", e.target.value)} className="w-full px-1 py-0.5 text-xs" /></td>
                <td className="border border-gray-200"><input value={p.pack_size} onChange={(e) => updateProductLine(idx, "pack_size", e.target.value)} className="w-20 px-1 py-0.5 text-xs" /></td>
                <td className="border border-gray-200"><input type="number" step="0.01" value={p.qty} onChange={(e) => updateProductLine(idx, "qty", parseFloat(e.target.value) || 0)} className="w-14 px-1 py-0.5 text-xs text-right" /></td>
                <td className="border border-gray-200"><input type="number" step="0.01" value={p.unit_price} onChange={(e) => updateProductLine(idx, "unit_price", parseFloat(e.target.value) || 0)} className="w-20 px-1 py-0.5 text-xs text-right" /></td>
                <td className="border border-gray-200"><input type="number" step="0.01" value={p.line_total} onChange={(e) => updateProductLine(idx, "line_total", parseFloat(e.target.value) || 0)} className="w-20 px-1 py-0.5 text-xs text-right" /></td>
                <td className="border border-gray-200 text-center"><button onClick={() => removeProductLine(idx)} className="text-[#A61B1B]"><X className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button size="sm" onClick={addProductLine} variant="outline" className="mt-2">
        <Plus className="w-4 h-4 mr-1" /> Add Line
      </Button>
    </div>
  );
}

function ComparisonView({ comparison, onClose }) {
  const fmtPrice = (n) => `$${(n || 0).toFixed(2)}`;
  return (
    <div className="bg-white rounded-lg p-3" data-testid="comparison-view">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="font-bold text-[#1A1A1A] flex items-center gap-2"><GitCompare className="w-4 h-4" /> Price Comparison</h3>
          <p className="text-xs text-[#666]">
            <span className="font-medium">Newer:</span> {comparison.invoice_a.date || comparison.invoice_a.uploaded_at?.slice(0,10)} #{comparison.invoice_a.number || "—"}
            <span className="mx-2">vs</span>
            <span className="font-medium">Older:</span> {comparison.invoice_b.date || comparison.invoice_b.uploaded_at?.slice(0,10)} #{comparison.invoice_b.number || "—"}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
      </div>

      <div className="grid grid-cols-5 gap-2 mb-3 text-xs">
        <SummaryPill label="Price Hikes" value={comparison.summary.hikes} color="bg-[#A61B1B]" />
        <SummaryPill label="Price Cuts" value={comparison.summary.cuts} color="bg-[#1A5E1F]" />
        <SummaryPill label="Unchanged" value={comparison.summary.unchanged} color="bg-gray-500" />
        <SummaryPill label="New Items" value={comparison.summary.new} color="bg-[#2563EB]" />
        <SummaryPill label="Dropped" value={comparison.summary.dropped} color="bg-gray-700" />
      </div>

      <Tabs defaultValue="hikes">
        <TabsList className="bg-gray-100 mb-2">
          <TabsTrigger value="hikes" className="data-[state=active]:bg-[#A61B1B] data-[state=active]:text-white gap-1"><TrendingUp className="w-3.5 h-3.5" />Hikes ({comparison.summary.hikes})</TabsTrigger>
          <TabsTrigger value="cuts" className="data-[state=active]:bg-[#1A5E1F] data-[state=active]:text-white gap-1"><TrendingDown className="w-3.5 h-3.5" />Cuts ({comparison.summary.cuts})</TabsTrigger>
          <TabsTrigger value="new" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">New ({comparison.summary.new})</TabsTrigger>
          <TabsTrigger value="dropped" className="data-[state=active]:bg-gray-700 data-[state=active]:text-white">Dropped ({comparison.summary.dropped})</TabsTrigger>
        </TabsList>

        <TabsContent value="hikes">
          <PriceChangeTable rows={comparison.hikes} positive />
        </TabsContent>
        <TabsContent value="cuts">
          <PriceChangeTable rows={comparison.cuts} positive={false} />
        </TabsContent>
        <TabsContent value="new">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-[#1A1A1A] text-white">
                <tr><th className="px-2 py-1 text-left">SKU</th><th className="px-2 py-1 text-left">UPC</th><th className="px-2 py-1 text-left">Description</th><th className="px-2 py-1 text-right">Price</th></tr>
              </thead>
              <tbody>
                {comparison.new.map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-2 py-1">{r.current.sku}</td>
                    <td className="px-2 py-1">{r.current.upc}</td>
                    <td className="px-2 py-1">{r.current.description}</td>
                    <td className="px-2 py-1 text-right">{fmtPrice(r.current.unit_price)}</td>
                  </tr>
                ))}
                {comparison.new.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-[#666]">None</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="dropped">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-[#1A1A1A] text-white">
                <tr><th className="px-2 py-1 text-left">SKU</th><th className="px-2 py-1 text-left">UPC</th><th className="px-2 py-1 text-left">Description</th><th className="px-2 py-1 text-right">Last Price</th></tr>
              </thead>
              <tbody>
                {comparison.dropped.map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="px-2 py-1">{r.previous.sku}</td>
                    <td className="px-2 py-1">{r.previous.upc}</td>
                    <td className="px-2 py-1">{r.previous.description}</td>
                    <td className="px-2 py-1 text-right">{fmtPrice(r.previous.unit_price)}</td>
                  </tr>
                ))}
                {comparison.dropped.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-[#666]">None</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryPill({ label, value, color }) {
  return (
    <div className={`${color} text-white rounded-lg p-2 text-center`}>
      <div className="text-xl font-bold">{value}</div>
      <div className="text-[10px] opacity-90">{label}</div>
    </div>
  );
}

function PriceChangeTable({ rows, positive }) {
  const fmtPrice = (n) => `$${(n || 0).toFixed(2)}`;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse" data-testid={positive ? "table-hikes" : "table-cuts"}>
        <thead className="bg-[#1A1A1A] text-white">
          <tr>
            <th className="px-2 py-1 text-left">SKU</th>
            <th className="px-2 py-1 text-left">UPC</th>
            <th className="px-2 py-1 text-left">Description</th>
            <th className="px-2 py-1 text-left">Pack</th>
            <th className="px-2 py-1 text-right">Old</th>
            <th className="px-2 py-1 text-right">New</th>
            <th className="px-2 py-1 text-right">Δ</th>
            <th className="px-2 py-1 text-right">%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b hover:bg-gray-50">
              <td className="px-2 py-1 font-mono">{r.sku}</td>
              <td className="px-2 py-1 font-mono">{r.upc}</td>
              <td className="px-2 py-1">{r.description}</td>
              <td className="px-2 py-1">{r.pack_size}</td>
              <td className="px-2 py-1 text-right text-[#666]">{fmtPrice(r.old_price)}</td>
              <td className="px-2 py-1 text-right font-bold">{fmtPrice(r.new_price)}</td>
              <td className={`px-2 py-1 text-right font-bold ${positive ? "text-[#A61B1B]" : "text-[#1A5E1F]"}`}>
                {positive ? "+" : ""}{fmtPrice(r.diff)}
              </td>
              <td className={`px-2 py-1 text-right font-bold ${positive ? "text-[#A61B1B]" : "text-[#1A5E1F]"}`}>
                {positive ? "+" : ""}{r.pct}%
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="text-center py-4 text-[#666]">None</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
